<template>
  <div class="incomes">
    <Actions :items="actionsHeader" :right="true" />
    <MobileTable
      v-model:selection="store.state.income.selectedIncomes"
      :label="listLabel"
      :columns="columns"
      :data="tableData"
    />
    <DesktopTable
      v-model:selection="store.state.income.selectedIncomes"
      :label="listLabel"
      :columns="columns"
      :data="tableData"
    />
    <p v-if="showEmptyState" class="empty-note">No hay ingresos para mostrar.</p>
    <Actions :items="actionsFooter" />
  </div>
</template>

<script>
import { onMounted, reactive, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import store from "../../store";
import Actions from "../../components/shared/Actions.vue";
import MobileTable from "../../widgets/tables/MobileTable.vue";
import DesktopTable from "../../widgets/tables/DesktopTable.vue";
import { openMode, actions } from "../../constants";

export default {
  components: { MobileTable, DesktopTable, Actions },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const selectedBuilding = computed(() => store.state.general.selectedBuilding);
    const buildingId = computed(() => {
      const value = selectedBuilding.value;
      if (!value) return null;
      return typeof value === "object" ? value.id : value;
    });
    const closures = computed(() => store.state.commonExpense?.closures || []);

    const isPeriodClosed = (dateStr) => {
      if (!dateStr || !buildingId.value || !closures.value.length) return false;
      const parsed = new Date(dateStr);
      if (Number.isNaN(parsed.getTime())) return false;
      const month = parsed.getUTCMonth() + 1;
      const year = parsed.getUTCFullYear();
      return closures.value.some(
        (closure) =>
          Number(closure.buildingId) === Number(buildingId.value) &&
          Number(closure.month) === month &&
          Number(closure.year) === year &&
          closure.status === "closed"
      );
    };

    const loadClosures = (targetBuildingId = buildingId.value) => {
      if (!targetBuildingId) return Promise.resolve();
      return store.dispatch(actions.commonExpenseActions.findAllClosure, {
        buildingId: targetBuildingId,
      });
    };

    const formatPayerType = (payerType, fallbackResidentType) => {
      const value = payerType || fallbackResidentType;
      if (value === "owner") return "Propietario";
      if (value === "tenant") return "Inquilino";
      if (value === "mixed") return "Mixto";
      return "Sin asignar";
    };

    const tableData = computed(() => {
      const rows = [...(store.state.income.incomes || [])].sort((a, b) => {
        const numberA = Number(a.number) || 0;
        const numberB = Number(b.number) || 0;
        return numberB - numberA;
      });
      return rows.map((item) => ({
        id: item.id,
        number: item.number,
        date: item.date,
        apartment: item.apartment?.number || item.apartment?.name || "",
        account: item.account?.name || "",
        paymentMethod: item.paymentMethod?.name || "",
        amount: item.amount,
        payerType: formatPayerType(item.payerType, item.residentType),
        comment: item.comment || "",
      }));
    });

    const listLabel = computed(
      () => `${route.name || "Ingresos"} (${tableData.value.length})`
    );

    const columns = reactive([
      { field: "number", header: "Número", sort: true },
      { field: "date", header: "Fecha", sort: true, date: true },
      { field: "apartment", header: "Apartamento" },
      { field: "account", header: "Cuenta" },
      { field: "paymentMethod", header: "Forma de pago" },
      { field: "amount", header: "Monto", currency: true },
      { field: "payerType", header: "Tipo de usuario" },
      { field: "comment", header: "Comentario" },
    ]);

    const showEmptyState = computed(
      () => !store.state.loading && !tableData.value.length && Boolean(buildingId.value)
    );

    const ensureBreadcrumb = () => {
      const exists = store.state.general.breadcrumbs.find((item) => item.to === route.path);
      if (!exists) {
        store.commit("addBreadcrumb", { label: route.name, to: route.path });
      }
    };

    const notify = (type, message) => {
      if (!message) return;
      store.commit("setToast", { type, message });
    };

    const loadIncomes = async () => {
      const currentBuildingId = buildingId.value;
      if (!currentBuildingId) {
        return;
      }
      await Promise.all([
        loadClosures(currentBuildingId),
        store.dispatch(actions.incomeActions.findAll, {
          buildingId: currentBuildingId,
        }),
      ]);
    };

    const onCreate = () => {
      store.commit("setOpenMode", openMode.CREATE);
      router.push("/contabilidad/ingreso/nuevo");
    };

    const onModify = () => {
      if (store.state.income.selectedIncomes.length !== 1) {
        notify("warn", "Debe seleccionar un registro");
        return;
      }
      const selected = store.state.income.selectedIncomes[0] || null;
      if (selected) {
        store.commit("setCurrentIncome", selected);
      }
      store.commit("setOpenMode", openMode.MODIFY);
      router.push("/contabilidad/ingreso/modificar");
    };

    const onDelete = async () => {
      if (!store.state.income.selectedIncomes.length) {
        notify("warn", "Debe seleccionar al menos un registro");
        return;
      }
      await loadClosures();
      const lockedIncomes = store.state.income.selectedIncomes.filter((income) =>
        isPeriodClosed(income.date)
      );
      if (lockedIncomes.length) {
        const label = lockedIncomes
          .map((item) => item.number || item.id)
          .join(", ");
        notify(
          "warn",
          `No es posible eliminar ingresos correspondientes a periodos cerrados (${label}).`
        );
        return;
      }
      if (!confirm("Eliminar los ingresos seleccionados?")) return;
      store.commit("setLoading", true);
      try {
        const errors = [];
        for (const income of store.state.income.selectedIncomes) {
          const payload = {
            id: income.id,
            buildingId: buildingId.value,
          };
          try {
            const response = await store.dispatch(actions.incomeActions.delete, payload);
            if (!response?.ok) {
              errors.push(response?.message || `Ingreso ${income.number || income.id} no pudo eliminarse.`);
            }
          } catch (error) {
            console.error(error);
            errors.push(`Ingreso ${income.number || income.id} no pudo eliminarse.`);
          }
        }
        if (errors.length) {
          notify("warn", errors[0]);
        } else {
          notify("success", "Ingresos eliminados correctamente.");
        }
        store.commit("setSelectedIncomes", []);
      } finally {
        store.commit("setLoading", false);
      }
    };

    const onDownloadExcel = async () => {
      if (!buildingId.value) {
        alert("Debe seleccionar un edificio");
        return;
      }
      store.commit("setLoading", true);
      try {
        await store.dispatch(actions.incomeActions.downloadExcel, {
          buildingId: buildingId.value,
        });
      } finally {
        store.commit("setLoading", false);
      }
    };

    const goBankAccount = () => {
      router.push("/contabilidad/cuenta-bancaria/listado");
    };

    const actionsHeader = reactive([
      {
        name: "Descargar Excel",
        action: onDownloadExcel,
        hidden: false,
      },
    ]);

    const actionsFooter = reactive([
      {
        name: "Nuevo",
        action: onCreate,
        hidden: router.getRoutes().find((r) => r.path === "/contabilidad/ingreso/nuevo"),
      },
      {
        name: "Modificar",
        action: onModify,
        hidden: router.getRoutes().find((r) => r.path === "/contabilidad/ingreso/modificar"),
      },
      {
        name: "Eliminar",
        action: onDelete,
        hidden: true,
      },
      {
        name: "Cuentas bancarias",
        action: goBankAccount,
        hidden: router
          .getRoutes()
          .find((r) => r.path === "/contabilidad/cuenta-bancaria/listado"),
      },
    ]);

    onMounted(async () => {
      store.commit("setLoading", true);
      store.commit("setSelectedIncomes", []);
      ensureBreadcrumb();
      try {
        await loadIncomes();
      } finally {
        store.commit("setLoading", false);
      }
    });

    watch(buildingId, async (value, oldValue) => {
      if (value && value !== oldValue) {
        store.commit("setLoading", true);
        store.commit("setSelectedIncomes", []);
        try {
          await loadIncomes();
        } finally {
          store.commit("setLoading", false);
        }
      }
    });

    return {
      store,
      columns,
      tableData,
      actionsHeader,
      actionsFooter,
      listLabel,
      showEmptyState,
    };
  },
};
</script>

<style scoped>
.empty-note {
  margin: 1rem 0;
  text-align: center;
  color: #64748b;
}
</style>




















